import type { Actor } from "../shared/types";

export type Matter = {
  id: string;
  number?: string;
  title: string;
  business_type?: string;
  lead_id?: string;
  lead_name?: string;
  scope?: string;
  fee_note?: string;
  fee_amount?: number;
  currency?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
  revision?: number;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  parties?: Party[];
  members?: Member[];
  conflicts?:
    | ConflictHit[]
    | { hits?: ConflictHit[]; token?: string; revision?: number };
  decisions?: Record<string, unknown>[];
  [key: string]: unknown;
};
export type Party = {
  id?: string;
  party_id?: string;
  name: string;
  role: string;
  identity_code?: string;
};
export type Member = {
  user_id: string;
  name: string;
  identity?: string;
  role?: string;
  allocation?: string;
};
export type ConflictHit = {
  id?: string;
  hit_id?: string;
  name?: string;
  party_name?: string;
  role?: string;
  matter_id?: string;
  matter_number?: string;
  matter_title?: string;
  matter_status?: string;
  number?: string;
  title?: string;
  status?: string;
  source_matter_id?: string;
  lead_name?: string;
  source?: string;
  resolution?: string;
  classification?: "history" | "review" | string;
  [key: string]: unknown;
};
export type FileRecord = {
  id: string;
  matter_id?: string;
  name?: string;
  file_name?: string;
  content_type?: string;
  size?: number;
  category?: string;
  status?: string;
  created_at?: string;
  uploaded_by_name?: string;
  revision?: number;
  document_id?: string;
  current?: boolean;
  failure_message?: string | null;
  [key: string]: unknown;
};
export type WorkItem = {
  id: string;
  kind?: string;
  title: string;
  detail?: string;
  matter_id?: string;
  number?: string;
  label?: string;
  status?: string;
  created_at?: string;
  href?: string;
};
export type Notification = {
  id: string;
  title: string;
  detail?: string;
  matter_id?: string;
  read_at?: string;
  created_at?: string;
};
export type Staff = {
  user_id: string;
  name: string;
  username?: string;
  identity: string;
  employment_status?: string;
  account_active?: boolean | number;
  phone?: string;
  license_number?: string;
  joined_on?: string;
  left_on?: string;
  must_change_password?: boolean;
  role_ids?: string[];
};
export type Role = {
  id: string;
  name: string;
  description?: string;
  permissions?: { action: string; scope: string }[];
  builtin?: boolean;
  preset?: number;
  user_count?: number;
};
export type RoleCatalog = {
  roles: Role[];
  actions: string[];
  scopes: Record<string, string[]>;
};

export class ApiError extends Error {
  status: number;
  constructor(message: string, status = 500) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (
    init.body &&
    !(init.body instanceof FormData) &&
    !headers.has("Content-Type")
  )
    headers.set("Content-Type", "application/json");
  const response = await fetch(path.startsWith("/api") ? path : `/api${path}`, {
    credentials: "include",
    ...init,
    headers,
  });
  const text = await response.text();
  let data: unknown = undefined;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = text;
  }
  if (!response.ok) {
    const message =
      typeof data === "object" && data && "error" in data
        ? String((data as { error: unknown }).error)
        : typeof data === "string" && data
          ? data
          : `请求失败（${response.status}）`;
    throw new ApiError(message, response.status);
  }
  return data as T;
}

export function asList<T>(
  data: unknown,
  keys: string[] = ["items", "data", "matters", "records"],
): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") {
    for (const key of keys) {
      const value = (data as Record<string, unknown>)[key];
      if (Array.isArray(value)) return value as T[];
    }
  }
  return [];
}

export const auth = {
  me: () => apiFetch<Actor>("/me"),
  signIn: (username: string, password: string) =>
    apiFetch<{ user?: Actor; actor?: Actor }>("/auth/sign-in/username", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<void>("/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  logout: () => apiFetch<void>("/logout", { method: "POST" }),
};

export const matters = {
  list: (
    params: {
      q?: string;
      type?: string;
      status?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(
      ([k, v]) => v !== undefined && v !== "" && query.set(k, String(v)),
    );
    return apiFetch<unknown>(`/matters${query.size ? `?${query}` : ""}`).then(
      (data) => asList<Matter>(data, ["matters", "items", "data"]),
    );
  },
  get: (id: string) => apiFetch<Matter>(`/matters/${encodeURIComponent(id)}`),
  create: async (payload: unknown) => {
    const created = await apiFetch<{ id: string }>("/matters", {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify(payload),
    });
    return apiFetch<Matter>(`/matters/${encodeURIComponent(created.id)}`);
  },
  update: async (id: string, payload: unknown) => {
    await apiFetch<unknown>(`/matters/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return apiFetch<Matter>(`/matters/${encodeURIComponent(id)}`);
  },
  setLead: (id: string, userId: string) =>
    apiFetch<unknown>(`/matters/${encodeURIComponent(id)}/lead`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    }),
  submit: (id: string) =>
    apiFetch<unknown>(`/matters/${encodeURIComponent(id)}/submit`, {
      method: "POST",
    }),
  decision: (id: string, payload: unknown) =>
    apiFetch<unknown>(`/matters/${encodeURIComponent(id)}/decision`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};

export const conflict = {
  check: (names: string[], matterId?: string) =>
    apiFetch<{ hits: ConflictHit[]; token: string; revision: number }>(
      "/conflict-checks",
      {
        method: "POST",
        body: JSON.stringify({
          names,
          ...(matterId ? { matter_id: matterId } : {}),
        }),
      },
    ),
};

export const parties = {
  search: (query: string) =>
    apiFetch<unknown>(`/parties?q=${encodeURIComponent(query)}`).then((data) =>
      asList<{ id: string; name: string; entity_type?: string }>(data, [
        "parties",
        "items",
        "data",
      ]),
    ),
  rename: (id: string, name: string, reason: string) =>
    apiFetch<unknown>(`/parties/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ name, reason }),
    }),
};

export const work = {
  items: () =>
    apiFetch<unknown>("/work-items").then((data) =>
      asList<WorkItem>(data, ["items", "work_items", "data"]),
    ),
  notifications: () =>
    apiFetch<unknown>("/notifications").then((data) =>
      asList<Notification>(data, ["notifications", "items", "data"]),
    ),
  readNotification: (id: string) =>
    apiFetch<void>(`/notifications/${encodeURIComponent(id)}/read`, {
      method: "POST",
    }),
};

export const files = {
  list: (matterId: string) =>
    apiFetch<unknown>(`/matters/${encodeURIComponent(matterId)}/files`).then(
      (data) => asList<FileRecord>(data, ["files", "items", "data"]),
    ),
  upload: (
    matterId: string,
    file: File,
    category?: string,
    onProgress?: (value: number) => void,
    documentId?: string,
    uploadOptions: {
      batchId: string;
      batchCount: number;
      batchSlot: number;
      idempotencyKey: string;
    } = {
      batchId: crypto.randomUUID(),
      batchCount: 1,
      batchSlot: 0,
      idempotencyKey: crypto.randomUUID(),
    },
  ) =>
    new Promise<FileRecord>((resolve, reject) => {
      const params = new URLSearchParams({
        matter_id: matterId,
        name: file.name,
        category: category || "other",
      });
      if (documentId) params.set("document_id", documentId);
      params.set("batch_id", uploadOptions.batchId);
      params.set("batch_count", String(uploadOptions.batchCount));
      params.set("batch_slot", String(uploadOptions.batchSlot));
      const request = new XMLHttpRequest();
      request.open("POST", `/api/files?${params.toString()}`);
      request.withCredentials = true;
      request.setRequestHeader(
        "Content-Type",
        file.type || "application/octet-stream",
      );
      request.setRequestHeader("Idempotency-Key", uploadOptions.idempotencyKey);
      request.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(event.loaded / event.total);
      };
      request.onerror = () => reject(new ApiError("网络连接失败"));
      request.onabort = () => reject(new ApiError("上传已取消"));
      request.onload = () => {
        let data: unknown;
        try {
          data = request.responseText
            ? JSON.parse(request.responseText)
            : undefined;
        } catch {
          data = undefined;
        }
        if (request.status >= 200 && request.status < 300)
          resolve(
            (data && typeof data === "object" && "file" in data
              ? (data as { file: FileRecord }).file
              : data) as FileRecord,
          );
        else
          reject(
            new ApiError(
              data && typeof data === "object" && "error" in data
                ? String((data as { error: unknown }).error)
                : `上传失败（${request.status}）`,
              request.status,
            ),
          );
      };
      request.send(file);
    }),
  contentUrl: (id: string, inline = false) =>
    `/api/files/${encodeURIComponent(id)}/content${inline ? "" : "?download=1"}`,
};

export const members = {
  add: (matterId: string, payload: unknown) =>
    apiFetch<Member>(`/matters/${encodeURIComponent(matterId)}/members`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  remove: (matterId: string, userId: string) =>
    apiFetch<void>(
      `/matters/${encodeURIComponent(matterId)}/members/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    ),
};

export const directory = {
  list: () =>
    apiFetch<unknown>("/directory").then((data) =>
      asList<Member>(data, ["users", "directory", "items", "data"]),
    ),
};

export const staff = {
  list: () =>
    apiFetch<unknown>("/staff").then((data) =>
      asList<Staff>(data, ["staff", "items", "data"]),
    ),
  create: (payload: unknown) =>
    apiFetch<Staff>("/staff", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  update: (userId: string, payload: unknown) =>
    apiFetch<Staff>(`/staff/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  resetPassword: (userId: string, password: string) =>
    apiFetch<unknown>(`/staff/${encodeURIComponent(userId)}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  status: (userId: string, active: boolean) =>
    apiFetch<unknown>(`/staff/${encodeURIComponent(userId)}/status`, {
      method: "POST",
      body: JSON.stringify({ active }),
    }),
};

export const roles = {
  list: () =>
    apiFetch<unknown>("/roles").then((data) => {
      const record =
        data && typeof data === "object"
          ? (data as Record<string, unknown>)
          : {};
      const scopes =
        record.scopes && typeof record.scopes === "object"
          ? (record.scopes as Record<string, string[]>)
          : {};
      const actions = Array.isArray(record.actions)
        ? record.actions.map(String)
        : Object.keys(scopes);
      return {
        roles: asList<Role>(data, ["roles", "items", "data"]),
        actions,
        scopes,
      } satisfies RoleCatalog;
    }),
  create: (payload: unknown) =>
    apiFetch<Role>("/roles", { method: "POST", body: JSON.stringify(payload) }),
  update: (id: string, payload: unknown) =>
    apiFetch<Role>(`/roles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
};

export const templates = {
  list: () =>
    apiFetch<unknown>("/templates").then((data) =>
      asList<Record<string, unknown>>(data, ["templates", "items", "data"]),
    ),
  upload: (
    file: File,
    category = "general",
    onProgress?: (value: number) => void,
  ) =>
    new Promise<Record<string, unknown>>((resolve, reject) => {
      const params = new URLSearchParams({ name: file.name, category });
      const request = new XMLHttpRequest();
      request.open("POST", `/api/templates?${params.toString()}`);
      request.withCredentials = true;
      request.setRequestHeader(
        "Content-Type",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      );
      request.setRequestHeader("Idempotency-Key", crypto.randomUUID());
      request.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(event.loaded / event.total);
      };
      request.onerror = () => reject(new ApiError("网络连接失败"));
      request.onload = () => {
        let data: unknown;
        try {
          data = request.responseText
            ? JSON.parse(request.responseText)
            : undefined;
        } catch {
          data = undefined;
        }
        if (request.status >= 200 && request.status < 300)
          resolve(
            (data && typeof data === "object" && "template" in data
              ? (data as { template: Record<string, unknown> }).template
              : data) as Record<string, unknown>,
          );
        else
          reject(
            new ApiError(
              data && typeof data === "object" && "error" in data
                ? String((data as { error: unknown }).error)
                : `模板上传失败（${request.status}）`,
              request.status,
            ),
          );
      };
      request.send(file);
    }),
  generate: async (payload: unknown) => {
    const response = await fetch("/api/document-generations", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      let message = `生成失败（${response.status}）`;
      try {
        const data = (await response.json()) as { error?: string };
        if (data.error) message = data.error;
      } catch {
        /* preserve status message */
      }
      throw new ApiError(message, response.status);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const plainName = disposition.match(/filename="([^"]+)"/i)?.[1];
    let fileName = encodedName
      ? decodeURIComponent(encodedName)
      : plainName || "标准文书-生成.docx";
    if (!fileName.toLowerCase().endsWith(".docx")) fileName += ".docx";
    return { file_id: "", download_url: url, file_name: fileName };
  },
};

export const stamp = {
  list: (matterId: string) =>
    apiFetch<unknown>(
      `/matters/${encodeURIComponent(matterId)}/stamp-requests`,
    ).then((data) =>
      asList<Record<string, unknown>>(data, [
        "stamp_requests",
        "requests",
        "items",
        "data",
      ]),
    ),
  create: (payload: unknown) => {
    const body = payload as Record<string, unknown>;
    return apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(String(body.matter_id))}/stamp-requests`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  decision: (
    matterId: string,
    id: string,
    outcome: "approve" | "reject",
    payload: unknown,
  ) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/stamp-requests/${encodeURIComponent(id)}/${outcome}`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  handle: (matterId: string, id: string, payload: unknown) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/stamp-requests/${encodeURIComponent(id)}/handle`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
};

export const archive = {
  get: (matterId: string) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/archive-request`,
    ),
  submit: (payload: unknown) => {
    const body = payload as Record<string, unknown>;
    return apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(String(body.matter_id))}/archive-request`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  confirm: (matterId: string, id: string, payload: unknown = {}) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/archive-request/${encodeURIComponent(id)}/confirm`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  return: (matterId: string, id: string, payload: unknown) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/archive-request/${encodeURIComponent(id)}/return`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
};

export const finance = {
  fees: (matterId: string) =>
    apiFetch<{
      fees?: Record<string, unknown>[];
      receipts?: Record<string, unknown>[];
      refunds?: Record<string, unknown>[];
      invoices?: Record<string, unknown>[];
    }>(`/matters/${encodeURIComponent(matterId)}/fees`).then((data) => ({
      fees: data.fees ?? [],
      receipts: data.receipts ?? [],
      refunds: data.refunds ?? [],
      invoices: data.invoices ?? [],
    })),
  expenses: (matterId: string) =>
    apiFetch<unknown>(`/matters/${encodeURIComponent(matterId)}/expenses`).then(
      (data) =>
        asList<Record<string, unknown>>(data, ["expenses", "items", "data"]),
    ),
  addFee: (matterId: string, payload: unknown) => {
    const body = payload as Record<string, unknown>;
    return apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/fees`,
      {
        method: "POST",
        body: JSON.stringify({
          amount_minor: body.amount,
          title: body.title || body.note || "案件收费",
          description: body.note,
          currency: body.currency || "CNY",
        }),
      },
    );
  },
  addReceipt: (matterId: string, feeId: string, payload: unknown) => {
    const body = payload as Record<string, unknown>;
    return apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/fees/${encodeURIComponent(feeId)}/receipts`,
      {
        method: "POST",
        body: JSON.stringify({
          amount_minor: body.amount,
          received_on: body.date,
          payment_method: body.payment_method,
          note: body.note,
          file_id: body.file_id,
        }),
      },
    );
  },
  addRefund: (matterId: string, feeId: string, payload: unknown) => {
    const body = payload as Record<string, unknown>;
    return apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/fees/${encodeURIComponent(feeId)}/refunds`,
      {
        method: "POST",
        body: JSON.stringify({
          amount_minor: body.amount,
          refunded_on: body.date,
          reason: body.note,
          file_id: body.file_id,
        }),
      },
    );
  },
  voidFee: (matterId: string, feeId: string, reason: string) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/fees/${encodeURIComponent(feeId)}/void`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
  voidReceipt: (matterId: string, recordId: string, reason: string) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/receipts/${encodeURIComponent(recordId)}/void`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
  voidRefund: (matterId: string, recordId: string, reason: string) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/refunds/${encodeURIComponent(recordId)}/void`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
  addExpense: (matterId: string, payload: unknown) => {
    const body = payload as Record<string, unknown>;
    return apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/expenses`,
      {
        method: "POST",
        body: JSON.stringify({
          amount_minor: body.amount,
          description: body.description || body.note || "案件经费",
          category: body.category,
          currency: body.currency || "CNY",
          expense_date:
            body.expense_date || new Date().toISOString().slice(0, 10),
          reimbursement_note: body.reimbursement_note,
          reimbursed_on: body.reimbursed_on,
          file_id: body.file_id,
        }),
      },
    );
  },
  updateExpense: (matterId: string, expenseId: string, payload: unknown) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/expenses/${encodeURIComponent(expenseId)}`,
      { method: "PATCH", body: JSON.stringify(payload) },
    ),
  voidExpense: (matterId: string, expenseId: string, reason: string) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/expenses/${encodeURIComponent(expenseId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ status: "void", void_reason: reason }),
      },
    ),
  requestInvoice: (matterId: string, payload: unknown) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/invoices`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  confirmInvoice: (matterId: string, invoiceId: string, payload: unknown) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/invoices/${encodeURIComponent(invoiceId)}/confirm`,
      { method: "POST", body: JSON.stringify(payload) },
    ),
  voidInvoice: (matterId: string, invoiceId: string, reason: string) =>
    apiFetch<Record<string, unknown>>(
      `/matters/${encodeURIComponent(matterId)}/invoices/${encodeURIComponent(invoiceId)}/void`,
      { method: "POST", body: JSON.stringify({ reason }) },
    ),
};

export const system = {
  backupStatus: () =>
    apiFetch<Record<string, unknown>>("/system/backup-status"),
};

export { apiFetch };
